/**
 * In-memory per-dashboard filter values store.
 *
 * Keyed by dashboard ID → variable → value.
 * Survives client-side navigation; cleared on hard page reload.
 *
 * Same pattern as `scrollStore.ts`.
 */

/** Covers all filter value shapes: categorical, boolean, date/numeric ranges. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FilterValue = string | string[] | boolean | Record<string, unknown> | null;
export type FilterMap = Map<string, FilterValue>;

const store = new Map<string, FilterMap>();

/** Get the current filter values for a dashboard (returns a new Map copy). */
export function getFilterValues(dashboardId: string): FilterMap {
  return new Map(store.get(dashboardId) ?? []);
}

/** Set a single filter value for a dashboard. */
export function setFilterValue(
  dashboardId: string,
  variable: string,
  value: FilterValue,
): void {
  let map = store.get(dashboardId);
  if (!map) {
    map = new Map();
    store.set(dashboardId, map);
  }
  map.set(variable, value);
}

/** Remove a single filter value (reset to default). */
export function deleteFilterValue(
  dashboardId: string,
  variable: string,
): void {
  store.get(dashboardId)?.delete(variable);
}

/** Clear all filter values for a dashboard (reset all). */
export function clearFilterValues(dashboardId: string): void {
  store.get(dashboardId)?.clear();
}

/**
 * Flatten a per-variable filter values map into a flat record for the
 * chart-execution API.
 *
 * Range filters (date_range, numeric) are stored as nested objects
 * `{ date_start: ..., date_end: ... }` under the variable key `"date"`.
 * The backend expects flat keys, so this spreads them out.
 * Arrays / primitives (categorical, boolean) keep the variable key.
 */
export function flattenFilterValues(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [variable, value] of Object.entries(values)) {
    if (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        out[k] = v;
      }
    } else {
      out[variable] = value;
    }
  }
  return out;
}
